import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { AuthService } from 'src/app/services/auth-service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { PathResolveService } from 'src/app/services/path-resolve.service';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { OrganizationUsersApiService } from 'src/app/data-transfer/services/organization-users-api-service';
import { OrganizationClaimApiService } from 'src/app/data-transfer/services/organization-claim-api-service';
import { UserGroupsApiService } from 'src/app/data-transfer/services/user-groups-api-service';
import { OrganizationFeaturesApiService } from 'src/app/data-transfer/services/organization-features-api-service';
import { OrganizationDto } from 'src/app/data-transfer/entities/organization-dto';
import { PASSWORD_PROVIDER_NAME } from 'src/app/data-transfer/entities/login-method-dto';
import { OrganizationUserDto } from 'src/app/data-transfer/entities/organization-user-dto';
import { EMPTY_GUID } from 'src/app/data-transfer/entities/doc-entities/dataset-profile-dto';
import { OrganizationUserGroupDto } from 'src/app/data-transfer/entities/user-group-entities/organization-user-group-dto';
import { ColumnDefinitionDto, UserProfileDto } from 'src/app/data-transfer/entities/user-profile-dto';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { DialogActions } from 'src/app/model/dictionary';
import { PAGE_SIZE_OPTIONS } from 'src/app/model/pagination-defaults';
import { SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { SimpleConfirmDialogComponent } from 'src/app/components/dialogs/simple-confirm-dialog/simple-confirm-dialog.component';
import { ColumnOrganizerDialogComponent, ColumnOrganizerDialogData } from 'src/app/components/dialogs/column-organizer-dialog/column-organizer-dialog.component';
import { ChangePasswordDialogComponent, PasswordDialogData } from 'src/app/components/dialogs/change-password-dialog/change-password-dialog.component';
import { getApiErrorMessage } from '../admin-api-error';
import { isLimitReached } from '../organization-limits';
import { ADMIN_USER_ROLE_ICONS, AdminUserRole, getUserDisplayName, getUserRole } from '../organization-user.util';
import { AdminUserDialogComponent, AdminUserDialogData, AdminUserDialogResult, AdminUserDialogResultData } from '../admin-user-dialog/admin-user-dialog.component';
import {
  AdminLeadAdminTransferDialogComponent,
  AdminLeadAdminTransferDialogData,
  AdminLeadAdminTransferDialogResult,
} from '../admin-lead-admin-transfer-dialog/admin-lead-admin-transfer-dialog.component';

const USER_PROFILE_COLUMNS_KEY = 'organizationAdminUsers';
const DEFAULT_VISIBLE_COLUMNS = ['email', 'userName', 'groups', 'role', 'premium', 'status'];

export const MANAGEABLE_USER_COLUMNS: ColumnDefinitionDto[] = [
  { key: 'email', labelKey: 'organizationAdmin.users.columns.email' },
  { key: 'userName', labelKey: 'dataManagement.userData.columns.name' },
  { key: 'groups', labelKey: 'organizationAdmin.users.columns.groups' },
  { key: 'role', labelKey: 'organizationAdmin.users.columns.role' },
  { key: 'premium', labelKey: 'organizationAdmin.users.columns.premium' },
  { key: 'status', labelKey: 'organizationAdmin.users.columns.status' },
];

export interface UserRow {
  user: OrganizationUserDto;
  name: string;
  groupNames: string;
  hasNoGroup: boolean;
  role: AdminUserRole;
  roleIcon: string;
  isSelf: boolean;
  isSelfLeadAdmin: boolean;
  hasHigherRights: boolean;
  premiumLimitReached: boolean;
  isPending: boolean;
}

const emptyUser = (): OrganizationUserDto => ({
  id: EMPTY_GUID,
  userName: '',
  firstName: '',
  lastName: '',
  email: '',
  isDeleted: false,
  isInactive: false,
  isPremiumUser: false,
  isOrganizationLeadAdmin: false,
  isOrganizationAdmin: false,
  isOrganizationUserManager: false,
  isOrganizationPartnershipManager: false,
  isPasswordLoginAllowed: false,
  hasSystemAdministrationRoles: false,
  userGroupIds: null,
});

const emptyRow = (): UserRow => ({
  user: emptyUser(),
  name: '',
  groupNames: '',
  hasNoGroup: false,
  role: AdminUserRole.Member,
  roleIcon: ADMIN_USER_ROLE_ICONS[AdminUserRole.Member],
  isSelf: false,
  isSelfLeadAdmin: false,
  hasHigherRights: false,
  premiumLimitReached: false,
  isPending: false,
});

@Component({
  selector: 'app-admin-users',
  templateUrl: './admin-users.component.html',
  styleUrls: ['./admin-users.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminUsersComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly userApiService = inject(UserApiService);
  private readonly organizationUsersApiService = inject(OrganizationUsersApiService);
  private readonly organizationClaimApiService = inject(OrganizationClaimApiService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userGroupsApiService = inject(UserGroupsApiService);
  private readonly organizationFeaturesApiService = inject(OrganizationFeaturesApiService);

  protected readonly manageableColumns = MANAGEABLE_USER_COLUMNS;
  protected readonly AdminUserRole = AdminUserRole;

  private readonly claimsReloaded = toSignal(this.authService.claimsReloaded$, { equal: () => false });
  protected readonly canEdit = computed(() => {
    this.claimsReloaded();
    return this.authService.canManageOrganization();
  });
  protected readonly isLeadAdmin = computed(() => {
    this.claimsReloaded();
    return this.authService.hasUserClaims() && this.authService.isLeadAdmin();
  });
  private readonly currentUserId = computed(() => {
    this.claimsReloaded();
    return this.authService.hasUserClaims() ? this.authService.getUserExternalId() : EMPTY_GUID;
  });

  protected readonly searchControl = new FormControl('', { nonNullable: true });
  private readonly searchTerm = toSignal(
    this.searchControl.valueChanges.pipe(
      debounceTime(200),
      map(term => term.trim().toLowerCase()),
      distinctUntilChanged()
    ),
    { initialValue: '' }
  );
  protected readonly highlightedUserId = signal<string | null>(null);

  private readonly organization = signal(new OrganizationDto());
  private readonly users = signal<OrganizationUserDto[]>([]);
  private readonly allGroups = signal<OrganizationUserGroupDto[]>([]);
  private readonly loginMethods = signal<string[]>([PASSWORD_PROVIDER_NAME]);
  private readonly isPasswordLoginDisabled = computed(() => !this.loginMethods().includes(PASSWORD_PROVIDER_NAME));
  private readonly pendingUserIds = signal<ReadonlySet<string>>(new Set());
  private readonly visibleColumns = signal<string[]>([...DEFAULT_VISIBLE_COLUMNS]);
  private userProfile: UserProfileDto = {};
  protected readonly showDeactivated = signal(false);
  protected readonly loadFailed = signal(false);
  private readonly isLoadingSignal = signal(true);
  protected readonly isLoading = this.isLoadingSignal.asReadonly();
  protected readonly pageSizeOptions = PAGE_SIZE_OPTIONS.DEFAULT;
  protected readonly pageIndex = signal(0);
  protected readonly pageSize = signal(this.pageSizeOptions[0]);

  private readonly placeholderRows = computed<UserRow[]>(() => (this.isLoadingSignal() ? Array.from({ length: this.pageSize() }, emptyRow) : []));

  private readonly premiumCount = computed(() => this.users().filter(user => user.isPremiumUser).length);
  private readonly activeUsers = computed(() => this.users().filter(user => !user.isDeleted && !user.isInactive));
  private readonly isPremiumLimitReachedSignal = computed(() => isLimitReached(this.premiumCount(), this.organization().maxPremiumUserCount));
  private readonly isUserLimitReachedSignal = computed(() => isLimitReached(this.activeUsers().length, this.organization().maxUserCount));

  protected readonly rows = computed<UserRow[]>(() => {
    if (this.isLoadingSignal()) {
      return this.placeholderRows();
    }

    const premiumLimitReached = this.isPremiumLimitReachedSignal();
    const currentUserId = this.currentUserId();
    const pendingUserIds = this.pendingUserIds();
    const hasHigherRights = this.createHigherRightsCheck();
    const groupNamesById = new Map(this.allGroups().map(group => [group.id, group.name]));
    const term = this.searchTerm();
    return this.users()
      .filter(user => this.showDeactivated() || !user.isDeleted)
      .filter(user => !term || [getUserDisplayName(user), user.email, user.userName].some(value => value?.toLowerCase().includes(term)))
      .sort((a, b) => Number(b.id === currentUserId) - Number(a.id === currentUserId))
      .map(user => {
        const role = getUserRole(user);
        const groupNames = (user.userGroupIds ?? [])
          .map(id => groupNamesById.get(id))
          .filter((name): name is string => !!name);
        return {
          user,
          groupNames: groupNames.join(', '),
          hasNoGroup: groupNames.length === 0,
          name: getUserDisplayName(user),
          role,
          roleIcon: ADMIN_USER_ROLE_ICONS[role],
          isSelf: user.id === currentUserId,
          isSelfLeadAdmin: role === AdminUserRole.Lead && user.id === currentUserId,
          hasHigherRights: hasHigherRights(user),
          premiumLimitReached: !user.isPremiumUser && premiumLimitReached,
          isPending: pendingUserIds.has(user.id),
        };
      });
  });

  protected readonly effectivePageIndex = computed(() => Math.min(this.pageIndex(), Math.max(0, Math.ceil(this.rows().length / this.pageSize()) - 1)));

  protected readonly pagedRows = computed(() => {
    const start = this.effectivePageIndex() * this.pageSize();
    return this.rows().slice(start, start + this.pageSize());
  });

  protected readonly displayedColumns = computed(() => [
    'name',
    ...this.manageableColumns.map(column => column.key).filter(key => this.visibleColumns().includes(key)),
    'actions',
  ]);

  protected readonly trackRow = (index: number, row: UserRow): string | number => (row.user.id !== EMPTY_GUID ? row.user.id : index);

  constructor() {
    this.searchControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.pageIndex.set(0));
  }

  ngOnInit(): void {
    forkJoin({
      organization: this.userApiService.getOwnOrganization(true),
      profile: this.userApiService.getUserProfile(true),
      users: this.canEdit()
        ? this.organizationUsersApiService.getUsers(true).pipe(
            catchError((error: HttpErrorResponse) => {
              this.loadFailed.set(true);
              this.snackbarService.showError(getApiErrorMessage(error));
              return of([] as OrganizationUserDto[]);
            })
          )
        : of([] as OrganizationUserDto[]),
      groups: this.canEdit() ?this.userGroupsApiService.getGroups(true).pipe(catchError(error => this.onOptionalLoadFailed(error, [] as OrganizationUserGroupDto[]))) : of([] as OrganizationUserGroupDto[]),
      features: this.canEdit() ? this.organizationFeaturesApiService.getFeatures(true).pipe(catchError(error => this.onOptionalLoadFailed(error, null))) : of(null),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ organization, profile, users, groups, features }) => {
          this.organization.set(organization);
          this.users.set(users);
          this.allGroups.set(groups.filter(group => !group.isRootGroup));
          if (features) {
            this.loginMethods.set(features.loginMethods);
          }

          this.userProfile = profile;
          const savedColumns = profile[USER_PROFILE_COLUMNS_KEY];
          if (savedColumns?.length) {
            this.visibleColumns.set(savedColumns);
          }
          this.isLoadingSignal.set(false);
        },
        error: (error: HttpErrorResponse) => {
          this.loadFailed.set(true);
          this.snackbarService.showError(getApiErrorMessage(error));
          this.isLoadingSignal.set(false);
        },
      });
  }

  protected onPageChanged(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
  }

  protected toggleShowDeactivated(): void {
    this.showDeactivated.update(show => !show);
    this.pageIndex.set(0);
  }

  protected openColumnOrganizer(): void {
    const data: ColumnOrganizerDialogData = {
      columnCatalog: this.manageableColumns,
      visibleColumns: [...this.visibleColumns()],
      entityLabelKey: 'organizationAdmin.users.title',
    };

    this.dialog
      .open(ColumnOrganizerDialogComponent, getDialogConfig(data, '600px'))
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: string[]) => {
        if (!result) return;

        this.visibleColumns.set(result);
        this.userProfile = { ...this.userProfile, [USER_PROFILE_COLUMNS_KEY]: result };
        this.userApiService
          .setUserProfile(this.userProfile, true)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({ error: error => this.snackbarService.showError(getApiErrorMessage(error)) });
      });
  }

  protected togglePremium(user: OrganizationUserDto, event: MatSlideToggleChange): void {
    this.setPending(user.id, true);
    this.organizationUsersApiService
      .setPremium(user.id, event.checked, true)
      .pipe(
        finalize(() => this.setPending(user.id, false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => this.refreshUsers(),
        error: error => {
          event.source.checked = !event.checked;
          this.onRequestFailed(error);
        },
      });
  }

  protected toggleUserActive(row: UserRow): void {
    const user = row.user;
    if (row.isSelfLeadAdmin && !user.isDeleted) {
      this.openTransferLeadAdminDialog(user);
      return;
    }

    if (user.isDeleted) {
      this.saveUserStatus(this.organizationUsersApiService.restoreUser(user.id, true));
      return;
    }

    const dialogData: SimpleDialogData = {
      title: this.translocoService.translate('organizationAdmin.users.actions.deactivate'),
      messages: [this.translocoService.translate('organizationAdmin.users.deactivateConfirm.message', { name: row.name || user.email })],
      icon: 'person_off',
    };
    this.dialog
      .open(SimpleConfirmDialogComponent, getDialogConfig(dialogData, '480px'))
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result?.event !== DialogActions.CONFIRM) return;
        this.saveUserStatus(this.organizationUsersApiService.deleteUser(user.id, true));
      });
  }

  private saveUserStatus(request: Observable<void>): void {
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.onSaved(),
      error: error => this.onRequestFailed(error),
    });
  }

  private openTransferLeadAdminDialog(currentLeadAdmin: OrganizationUserDto): void {
    const data: AdminLeadAdminTransferDialogData = {
      currentLeadAdmin,
      candidates: this.activeUsers().filter(user => user.isOrganizationAdmin && user.id !== currentLeadAdmin.id),
    };

    this.dialog
      .open(AdminLeadAdminTransferDialogComponent, getDialogConfig(data, '480px'))
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: AdminLeadAdminTransferDialogResult) => {
        if (result?.event !== DialogActions.CONFIRM) return;

        this.organizationClaimApiService
          .handOverLeadAdmin(result.data.newLeadAdmin.id, true)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => this.onLeadAdminHandedOver(),
            error: error => this.onRequestFailed(error),
          });
      });
  }

  protected resetPassword(user: OrganizationUserDto): void {
    const data = new PasswordDialogData(user.id, getUserDisplayName(user), true);
    this.dialog
      .open(ChangePasswordDialogComponent, getDialogConfig(data, '600px'))
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result?.event !== DialogActions.CONFIRM) return;

        this.organizationUsersApiService
          .setPassword(user.id, { password: result.data.password }, true)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => this.snackbarService.showInfo(this.translateSuccess(), 3000),
            error: error => this.onRequestFailed(error),
          });
      });
  }

  protected openCreateUserDialog(): void {
    if (this.isUserLimitReachedSignal()) {
      this.snackbarService.showError('organizationAdmin.users.messages.userLimitReached');
      return;
    }

    const data: AdminUserDialogData = {
      mode: 'create',
      allGroups: this.allGroups(),
      existingEmails: this.getExistingEmails(),
      isLeadAdmin: this.isLeadAdmin(),
      premiumLimitReached: this.isPremiumLimitReachedSignal(),
      passwordLoginDisabled: this.isPasswordLoginDisabled(),
      emailLocked: false,
      save: result => this.createUser(result),
    };

    this.dialog.open(AdminUserDialogComponent, getDialogConfig(data, '600px'));
  }

  // Fails only if the user couldn't be created, so that the dialog stays open; the failures of the groups and roles are shown afterwards.
  private createUser({ firstName, lastName, email, password, isPremiumUser, role, groupIds }: AdminUserDialogResultData): Observable<unknown> {
    return this.organizationUsersApiService.createUser({ firstName, lastName, email, password, isPremiumUser }, true).pipe(
      switchMap(id => {
        const requests = this.createGroupChangeRequests(id, [], groupIds);
        if (role === AdminUserRole.Admin && this.isLeadAdmin()) {
          requests.push(this.setAdminClaims(id, true));
        }
        return this.runAll(requests).pipe(map(error => ({ id, error })));
      }),
      tap(({ id, error }) => (error ? this.onRequestFailed(error) : this.onSaved(id)))
    );
  }

  protected openEditUserDialog(row: UserRow): void {
    const user = row.user;
    const data: AdminUserDialogData = {
      mode: 'edit',
      user,
      role: row.role,
      allGroups: this.allGroups(),
      groupIds: user.userGroupIds ?? [],
      existingEmails: this.getExistingEmails(user),
      isLeadAdmin: this.isLeadAdmin(),
      premiumLimitReached: row.premiumLimitReached,
      passwordLoginDisabled: this.isPasswordLoginDisabled(),
      emailLocked: !user.isPasswordLoginAllowed || row.hasHigherRights || row.isSelf,
      nameLocked: row.hasHigherRights,
      isSelf: row.isSelf,
    };

    this.dialog
      .open(AdminUserDialogComponent, getDialogConfig(data, '600px'))
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: AdminUserDialogResult) => {
        if (result?.event !== DialogActions.CONFIRM) return;

        const { firstName, lastName, email, isPremiumUser, role, groupIds } = result.data;
        const requests = this.createGroupChangeRequests(user.id, user.userGroupIds ?? [], groupIds);
        if (firstName !== (user.firstName ?? '') || lastName !== (user.lastName ?? '')) {
          requests.push(this.organizationUsersApiService.changeName(user.id, { firstName, lastName }, true));
        }
        if (email !== user.email) {
          requests.push(this.organizationUsersApiService.changeEmail(user.id, { email }, true));
        }
        if (isPremiumUser !== user.isPremiumUser) {
          requests.push(this.organizationUsersApiService.setPremium(user.id, isPremiumUser, true));
        }
        if (this.isLeadAdmin() && role !== row.role) {
          requests.push(this.setAdminClaims(user.id, role !== AdminUserRole.Member));
        }
        if (requests.length === 0) return;

        this.runAll(requests)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(error => (error ? this.onRequestFailed(error) : this.onSaved()));
      });
  }

  private runAll(requests: Observable<unknown>[]): Observable<HttpErrorResponse | undefined> {
    if (requests.length === 0) return of(undefined);
    return forkJoin(requests.map(request => request.pipe(map(() => undefined), catchError((error: HttpErrorResponse) => of(error))))).pipe(
      map(results => results.find(result => result !== undefined))
    );
  }

  private setPending(userId: string, isPending: boolean): void {
    this.pendingUserIds.update(ids => {
      const next = new Set(ids);
      if (isPending) {
        next.add(userId);
      } else {
        next.delete(userId);
      }
      return next;
    });
  }

  private createGroupChangeRequests(userId: string, previousGroupIds: string[], groupIds: string[]): Observable<unknown>[] {
    const added = groupIds.filter(id => !previousGroupIds.includes(id));
    const removed = previousGroupIds.filter(id => !groupIds.includes(id));
    return [
      ...added.map(groupId => this.userGroupsApiService.addMembers(groupId, { userIds: [userId] }, true)),
      ...removed.map(groupId => this.userGroupsApiService.removeMembers(groupId, { userIds: [userId] }, true)),
    ];
  }

  private setAdminClaims(userId: string, value: boolean): Observable<unknown> {
    return this.organizationClaimApiService.setOrganizationClaims(
      { userId, isOrganizationAdmin: value, isOrganizationUserManager: value, isOrganizationPartnershipManager: value },
      true
    );
  }

  private createHigherRightsCheck(): (user: OrganizationUserDto) => boolean {
    if (!this.authService.hasUserClaims()) return () => true;
    const isLeadAdmin = this.authService.isLeadAdmin();
    const isAdmin = this.authService.isOrganizationAdmin();
    const isUserManager = this.authService.isOrganizationUserManager();
    const isPartnershipManager = this.authService.isOrganizationPartnershipManager();
    return user =>
      user.hasSystemAdministrationRoles ||
      (user.isOrganizationLeadAdmin && !isLeadAdmin) ||
      (user.isOrganizationAdmin && !isAdmin) ||
      (user.isOrganizationUserManager && !isUserManager) ||
      (user.isOrganizationPartnershipManager && !isPartnershipManager);
  }

  private async onLeadAdminHandedOver(): Promise<void> {
    try {
      await this.authService.reloadUserClaims();
    } catch (error) {
      this.snackbarService.showError(getApiErrorMessage(error as HttpErrorResponse));
      this.refreshUsers();
      return;
    }
    this.onSaved();
    if (!this.authService.canManageOrganization()) {
      void this.router.navigate([PathResolveService.getCockpitPath()]);
    }
  }

  private getExistingEmails(excludedUser?: OrganizationUserDto): Set<string> {
    return new Set(
      this.users()
        .filter(user => !user.isDeleted && user.id !== excludedUser?.id)
        .map(user => user.email.toLowerCase())
    );
  }

  private onSaved(createdUserId?: string): void {
    this.refreshUsers(createdUserId);
    this.snackbarService.showInfo(this.translateSuccess(), 3000);
  }

  private showUser(userId: string): void {
    const index = this.rows().findIndex(row => row.user.id === userId);
    if (index < 0) return;
    this.pageIndex.set(Math.floor(index / this.pageSize()));
    this.highlightedUserId.set(userId);
    setTimeout(() => this.highlightedUserId.set(null), 3000);
  }

  private onRequestFailed(error: HttpErrorResponse): void {
    this.snackbarService.showError(getApiErrorMessage(error));
    this.refreshUsers();
  }

  private refreshUsers(userIdToShow?: string): void {
    this.organizationUsersApiService
      .getUsers(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: users => {
          this.loadFailed.set(false);
          this.users.set(users);
          if (userIdToShow) {
            this.showUser(userIdToShow);
          }
        },
        error: error => {
          this.users.set([]);
          this.loadFailed.set(true);
          this.snackbarService.showError(getApiErrorMessage(error));
        },
      });
  }

  private onOptionalLoadFailed<T>(error: HttpErrorResponse, fallback: T): Observable<T> {
    this.snackbarService.showError(getApiErrorMessage(error));
    return of(fallback);
  }

  private translateSuccess(): string {
    return this.translocoService.translate('organizationAdmin.users.messages.saveSuccess');
  }
}
